import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { combineLatest, map, Observable, switchMap, take, tap } from 'rxjs';
import { VOID } from '@frontend-site/shared/utils';
import { AppVersionService } from './app-version.service';
import { ProjectStore, SessionRepository, SystemStore } from '@frontend-site/shared/core';
import { AngularModeNameById } from '@frontend-site/shared/data';

@Injectable()
export class CopyDetailsService {
    constructor(
        readonly session: SessionRepository,
        private readonly http: HttpClient,
        private readonly projectStore: ProjectStore,
        private readonly systemStore: SystemStore,
        private readonly appVersionService: AppVersionService
    ) {}

    handleCopyDetails(): Observable<any> {
        return combineLatest([this.getSystemDetails(), this.getProjectDetails(), this.appVersionService.getBuildVersion()]).pipe(
            map(([system, project, version]) => {
                return {
                    environment: location.origin,
                    user: this.session.applicationConfig?.user.email,
                    'system id': system?.systemId,
                    'system name': system?.systemName,
                    'project name': project?.projectName,
                    'project id': project?.projectId,
                    'angular mode': AngularModeNameById.get(this.session.applicationConfig?.angularMode),
                    'upload id': project?.uploadId,
                    'login link': project?.login,
                    'version#': version
                };
            }),
            tap((res) => {
                this.copyToClipboard(this.formatToText(res)).then(() => console.log('Copied!'));
            }),
            switchMap(() => VOID),
            take(1)
        );
    }

    getSystemDetails(): Observable<systemDetails | null> {
        return this.systemStore.systemDetails$.pipe(
            map((systemDetails) => {
                if (!!systemDetails) return { systemId: systemDetails.systemId, systemName: systemDetails.systemName };
                return null;
            })
        );
    }

    getProjectDetails(): Observable<projectDetails | null> {
        return this.projectStore.projectDetails$.pipe(
            map((projectDetails) => {
                if (!!projectDetails)
                    return {
                        projectName: projectDetails.projectName,
                        projectId: projectDetails.projectId,
                        uploadId: projectDetails.activeEtlId,
                        login: this.getLoginLink(projectDetails.projectId)
                    };
                return null;
            })
        );
    }

    formatToText(object: Object): string {
        return Object.entries(object)
            .filter(([key, val]) => !!val)
            .reduce((str: string, [key, val]) => `${str}${(key + ':').padEnd(15)}${val}\n`, '');
    }

    copyToClipboard(text: string) {
        return navigator.clipboard?.writeText(text);
    }

    getLoginLink(projectId: number): string {
        return (
            location.origin.replace('-appprod', '').replace('//', '//automation-dashboard-') +
            `/automation/newautomation/#/autoLogin/${projectId}`
        );
    }
}

class projectDetails {
    projectName: string = '';
    projectId: number = 0;
    uploadId: number = 0;
    login: string = '';
}

class systemDetails {
    systemId: number = 0;
    systemName: string = '';
}
